/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\systemGeneralApi.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the System General module.
────────────────────────────────────────────────────────────────*/


import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  SystemGeneralSettingsResponse,
  SystemGeneralSettingsPayload,
} from '../types/systemGeneral.types';

/**
 * Thrown when the stored domain ID no longer exists on this server
 * (e.g. fresh installation with different autoincrement IDs).
 */
export class InvalidDomainError extends Error {
  constructor() {
    super('Domain not found on this server');
    this.name = 'InvalidDomainError';
  }
}

/**
 * Fetch system general settings for a domain
 */
export async function fetchSystemGeneralSettings(
  domainId: number
): Promise<SystemGeneralSettingsResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/system-general/${domainId}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    // 400 or 404 = domain ID is stale / doesn't exist in this installation
    if (response.status === 400 || response.status === 404) {
      throw new InvalidDomainError();
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch system general settings');
  }

  return response.json();
}

/**
 * Update system general settings for a domain
 */
export async function updateSystemGeneralSettings(
  domainId: number,
  payload: SystemGeneralSettingsPayload
): Promise<SystemGeneralSettingsResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/system-general/${domainId}`),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update system general settings');
  }

  return response.json();
}

